import { useState } from 'react';
import {
    Grid,
    TextField,
    FormControl,
    FormGroup,
    Checkbox,
    FormControlLabel
} from '@material-ui/core';
import moment from 'moment';
import { Alert } from '@material-ui/lab';
import BookingFieldSelect from './atom/BookingFieldSelect';
import NumberTextbox from './atom/NumberTextbox';
import HeightTextbox from './atom/HeightTextbox';
import BookingFieldDate from './atom/BookingFieldDate';
import FormSection from './atom/FormSection';
import HotelFieldInput from './HotelFieldInput';
import LodgingFieldInput from './LodgingFieldInput';
import {
    isPhoneField,
    isEmailField,
    formatPhone,
    validatePhone,
    validateEmail
} from '../helpers/phoneUtils';

const lbsToKg = 1 / 2.20462;
const lbsToStone = 1 / 14;

const GuestExpansionPanel = ({
    guestLabel = 'Guest',
    guest,
    fields,
    onGuestChange,
    isAgent,
    defaultHotelId,
    // FareharborDirect lodging props
    activityId,
    availabilityPk
}) => {
    const [fieldErrors, setFieldErrors] = useState({});

    if (!fields || !fields.length) {
        return null;
    }

    const getFieldError = (field, value) => {
        if (!value) {
            return '';
        }
        if (isPhoneField(field.name)) {
            const err = validatePhone(value);
            return err && err !== 'Phone is required.' ? err : '';
        }
        if (isEmailField(field.name)) {
            return validateEmail(value) || '';
        }
        return '';
    };

    // Helper to get field value, with hotel prefill fallback
    const getFieldValue = field => {
        if (guest && guest[field.id] !== undefined) {
            return guest[field.id];
        }
        // Prefill hotel fields from defaultHotelId if not already set
        if (field.type === 'hotel' && defaultHotelId) {
            return defaultHotelId;
        }
        return '';
    };

    const finished = fields
        .filter(field => (field?.agentOnly && isAgent) || !field?.agentOnly)
        .every(field => {
            if (!field.required) {
                return true;
            }
            const value = getFieldValue(field);
            return value !== undefined && value !== null && value !== '';
        });

    return (
        <FormSection title={guestLabel}>
            {!finished && (
                <Alert severity="warning" style={{ width: '100%' }}>
                    There are some missing fields for this guest.
                </Alert>
            )}
            {fields
                .filter(
                    field => (field?.agentOnly === true && isAgent) || field?.agentOnly !== true
                )
                .map(field => (
                    <Grid item xs={12} sm={field.type === 'lodging' ? 12 : 6} key={field.id}>
                        <FormGroup className="mb-3">
                            <FormControl>
                                {(() => {
                                    switch (field.type) {
                                        case 'select':
                                            return (
                                                <BookingFieldSelect
                                                    field={field}
                                                    value={(guest && guest[field.id]) || ''}
                                                    onChange={ev =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: ev.target.value
                                                        })
                                                    }
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        case 'boolean':
                                            return (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            id={`boolean-${field.id}`}
                                                            checked={guest && guest[field.id]}
                                                            onChange={ev =>
                                                                onGuestChange({
                                                                    ...guest,
                                                                    [field.id]: ev.target.checked
                                                                })
                                                            }
                                                            disabled={field.readOnly}
                                                        />
                                                    }
                                                    label={`${field.name}${
                                                        field.required ? ' *' : ''
                                                    }`}
                                                />
                                            );
                                        case 'number':
                                            return (
                                                <NumberTextbox
                                                    label={field.name}
                                                    required={field.required}
                                                    value={guest && guest[field.id]}
                                                    options={
                                                        field.options
                                                            ? typeof field.options === 'string'
                                                                ? field.options
                                                                      .split(',')
                                                                      .map(e => e.trim())
                                                                : []
                                                            : []
                                                    }
                                                    setValue={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: value
                                                        })
                                                    }
                                                    postfix={guest && guest[`${field.id}_postfix`]}
                                                    setPostfix={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [`${field.id}_postfix`]: value
                                                        })
                                                    }
                                                    variant="standard"
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        case 'weight': {
                                            const postfix =
                                                guest && guest[`${field.id}_weight_postfix`];
                                            return (
                                                <NumberTextbox
                                                    label={field.name}
                                                    required={field.required}
                                                    value={Math.round(
                                                        guest && postfix === 'kg'
                                                            ? guest[field.id] * lbsToKg
                                                            : postfix === 'stone'
                                                            ? guest[field.id] * lbsToStone
                                                            : guest[field.id]
                                                    )}
                                                    options={['lbs', 'kg', 'stone']}
                                                    setValue={value => {
                                                        const val =
                                                            postfix === 'kg'
                                                                ? value / lbsToKg
                                                                : postfix === 'stone'
                                                                ? value / lbsToStone
                                                                : value;
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: val
                                                        });
                                                    }}
                                                    postfix={
                                                        guest && guest[`${field.id}_weight_postfix`]
                                                    }
                                                    setPostfix={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [`${field.id}_weight_postfix`]: value
                                                        })
                                                    }
                                                    variant="standard"
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        }
                                        case 'height':
                                            return (
                                                <HeightTextbox
                                                    label={field.name}
                                                    required={field.required}
                                                    value={guest && guest[field.id]}
                                                    setValue={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: value
                                                        })
                                                    }
                                                    postfix={
                                                        guest && guest[`${field.id}_height_postfix`]
                                                    }
                                                    setPostfix={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [`${field.id}_height_postfix`]: value
                                                        })
                                                    }
                                                    variant="standard"
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        case 'date':
                                            return (
                                                <BookingFieldDate
                                                    field={field}
                                                    required={field.required}
                                                    value={(guest && guest[field.id]) || null}
                                                    onChange={newDate =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: moment(newDate).format(
                                                                'YYYY-MM-DD'
                                                            )
                                                        })
                                                    }
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        case 'hotel': {
                                            const visibleIslands =
                                                window.clientData?.visibleIslands || [];
                                            return (
                                                <HotelFieldInput
                                                    value={getFieldValue(field)}
                                                    onChange={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: value
                                                        })
                                                    }
                                                    visibleIslands={visibleIslands}
                                                    required={field.required}
                                                    label={field.name}
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        }
                                        case 'lodging':
                                            return (
                                                <LodgingFieldInput
                                                    value={guest && guest[field.id]}
                                                    onChange={value =>
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: value
                                                        })
                                                    }
                                                    required={field.required}
                                                    label={field.name}
                                                    disabled={field.readOnly}
                                                    activityId={activityId}
                                                    availabilityPk={availabilityPk}
                                                />
                                            );
                                        default: {
                                            const isPhone = isPhoneField(field.name);
                                            const isEmail = isEmailField(field.name);
                                            const fieldValue = (guest && guest[field.id]) || '';
                                            const errorMsg = fieldErrors[field.id] || '';
                                            return (
                                                <TextField
                                                    id={`text-${field.id}`}
                                                    label={field.name}
                                                    type={
                                                        isPhone ? 'tel' : isEmail ? 'email' : 'text'
                                                    }
                                                    value={fieldValue}
                                                    onChange={ev => {
                                                        onGuestChange({
                                                            ...guest,
                                                            [field.id]: ev.target.value
                                                        });
                                                        if (errorMsg) {
                                                            setFieldErrors(prev => ({
                                                                ...prev,
                                                                [field.id]: ''
                                                            }));
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        if (isPhone && fieldValue) {
                                                            const formatted = formatPhone(
                                                                fieldValue
                                                            );
                                                            if (formatted !== fieldValue) {
                                                                onGuestChange({
                                                                    ...guest,
                                                                    [field.id]: formatted
                                                                });
                                                            }
                                                        }
                                                        const err = getFieldError(
                                                            field,
                                                            fieldValue
                                                        );
                                                        setFieldErrors(prev => ({
                                                            ...prev,
                                                            [field.id]: err
                                                        }));
                                                    }}
                                                    error={!!errorMsg}
                                                    helperText={errorMsg}
                                                    required={field.required}
                                                    disabled={field.readOnly}
                                                />
                                            );
                                        }
                                    }
                                })()}
                            </FormControl>
                        </FormGroup>
                    </Grid>
                ))}
        </FormSection>
    );
};

export default GuestExpansionPanel;
